/**
 * LabDashboardPage (Phase-2, Module 7 — lab admin landing)
 *
 * Quick stats + entry points to investigations and blood-test processing.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Grid, Button, Stack } from '@mui/material';
import { Biotech as InvIcon, WaterDrop as BloodIcon, Science as LabIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageScaffold, KPICard } from '../../components/common';
import { InvestigationApi, BloodTestApi } from '../../services/phase2Api';

const LabDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ assignedInv: 0, reportReadyInv: 0, bookedBt: 0, reportReadyBt: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [assigned, reportReadyInv, booked, reportReadyBt] = await Promise.all([
        InvestigationApi.listAll({ page: 0, size: 1, status: 'ASSIGNED' }),
        InvestigationApi.listAll({ page: 0, size: 1, status: 'REPORT_READY' }),
        BloodTestApi.listAll({ page: 0, size: 1, status: 'BOOKED' }),
        BloodTestApi.listAll({ page: 0, size: 1, status: 'REPORT_READY' }),
      ]);
      setStats({
        assignedInv: assigned?.totalElements ?? 0,
        reportReadyInv: reportReadyInv?.totalElements ?? 0,
        bookedBt: booked?.totalElements ?? 0,
        reportReadyBt: reportReadyBt?.totalElements ?? 0,
      });
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load stats'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PageScaffold title="Lab Dashboard" subtitle="Diagnostic laboratory operations" icon={<LabIcon />}
      loading={loading} error={error} onDismissError={() => setError(null)}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Assigned Investigations" value={stats.assignedInv} subtitle="Awaiting processing" icon={<InvIcon />} color="warning" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Reports Ready" value={stats.reportReadyInv} subtitle="Investigations" icon={<InvIcon />} color="info" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Booked Blood Tests" value={stats.bookedBt} subtitle="Awaiting confirmation" icon={<BloodIcon />} color="primary" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Blood Reports Ready" value={stats.reportReadyBt} subtitle="Awaiting completion" icon={<BloodIcon />} color="success" /></Grid>
      </Grid>
      <Stack direction="row" spacing={2}>
        <Button variant="contained" size="large" startIcon={<InvIcon />} onClick={() => navigate('/lab/investigations')} sx={{ textTransform: 'none', fontWeight: 600 }}>Process Investigations</Button>
        <Button variant="contained" size="large" color="error" startIcon={<BloodIcon />} onClick={() => navigate('/lab/blood-tests')} sx={{ textTransform: 'none', fontWeight: 600 }}>Process Blood Tests</Button>
        <Button variant="outlined" size="large" startIcon={<LabIcon />} onClick={() => navigate('/admin/laboratories')} sx={{ textTransform: 'none', fontWeight: 600 }}>Manage Catalog</Button>
      </Stack>
    </PageScaffold>
  );
};

export default LabDashboardPage;